import React, { FC, useEffect, useMemo } from "react";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import { useAppTheme } from "@/utils/theme";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AccountSetting, Setting } from "@/domain/settings.interface";
import { AccountSettingPayload } from "@/services/account-settings.service";
import { useSettingsCatalog } from "@/hooks/useSettingsCatalog";

type AccountSettingFormData = {
  settingId: string;
  valueString: string;
  valueNumber: number | string;
  valueBoolean: boolean;
  valueJson: string;
  active: boolean;
};

const DEFAULT_VALUES: AccountSettingFormData = {
  settingId: "",
  valueString: "",
  valueNumber: "",
  valueBoolean: false,
  valueJson: "",
  active: true,
};

const buildSchema = () =>
  yup.object().shape({
    settingId: yup.string().required("Setting is required"),
    valueString: yup.string().optional(),
    valueNumber: yup.mixed().optional(),
    valueBoolean: yup.boolean().required(),
    valueJson: yup.string().optional(),
    active: yup.boolean().required(),
  });

type AccountSettingModalProps = {
  open: boolean;
  onClose: () => void;
  accountSetting?: AccountSetting;
  onSave: (data: AccountSettingPayload) => void;
  isPending: boolean;
};

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value ?? "", null, 2);
  } catch {
    return "";
  }
};

const resolveSettingId = (accountSetting?: AccountSetting): string => {
  if (!accountSetting) return "";
  if (typeof accountSetting.settingId === "string") {
    return accountSetting.settingId;
  }
  return accountSetting.settingId?._id || "";
};

const AccountSettingModal: FC<AccountSettingModalProps> = ({
  open,
  onClose,
  accountSetting,
  onSave,
  isPending,
}) => {
  const theme = useAppTheme();
  const isEdit = Boolean(accountSetting);

  // Load the catalog to back the Autocomplete and to detect the `type`
  // of the currently selected setting so we can render the right input.
  const { settings, isLoadingSettings } = useSettingsCatalog({
    pageNumber: 0,
    pageSize: 500,
    sortField: "name",
    sortOrder: 1,
  });

  const availableSettings: Setting[] = useMemo(
    () => settings?.list || [],
    [settings]
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<AccountSettingFormData>({
    resolver: yupResolver(buildSchema()) as never,
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const selectedSettingId = watch("settingId");

  // Look up the currently selected setting in the catalog. If the backend
  // populated settingId as an object on an existing accountSetting, fall
  // back to that so we can still detect the type while the catalog loads.
  const selectedSetting: Setting | undefined = useMemo(() => {
    const fromCatalog = availableSettings.find(
      (s) => s._id === selectedSettingId
    );
    if (fromCatalog) return fromCatalog;
    if (accountSetting && typeof accountSetting.settingId === "object") {
      const populated = accountSetting.settingId as Setting;
      if (populated?._id === selectedSettingId) return populated;
    }
    return undefined;
  }, [selectedSettingId, availableSettings, accountSetting]);

  useEffect(() => {
    if (!open) return;
    if (accountSetting) {
      const settingId = resolveSettingId(accountSetting);
      // We don't know the type until we resolve the Setting, so seed all
      // raw fields from the stored value — the correct one will be used
      // once `selectedSetting` is known.
      const v = accountSetting.value;
      reset({
        settingId,
        valueString: typeof v === "string" ? v : "",
        valueNumber:
          typeof v === "number"
            ? v
            : v === null || v === undefined
              ? ""
              : typeof v === "string"
                ? v
                : "",
        valueBoolean: typeof v === "boolean" ? v : false,
        valueJson: typeof v === "object" && v !== null ? safeStringify(v) : "",
        active: !!accountSetting.active,
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [accountSetting, open, reset]);

  const onSubmit = (data: AccountSettingFormData) => {
    clearErrors("valueJson");

    if (!selectedSetting) {
      setError("settingId", {
        type: "manual",
        message: "Select a valid setting",
      });
      return;
    }

    let value: unknown = null;
    switch (selectedSetting.type) {
      case "string":
        value = data.valueString ?? "";
        break;
      case "number": {
        const raw = data.valueNumber;
        if (raw === "" || raw === null || raw === undefined) {
          value = null;
        } else {
          const n = typeof raw === "number" ? raw : Number(raw);
          value = isNaN(n) ? null : n;
        }
        break;
      }
      case "boolean":
        value = !!data.valueBoolean;
        break;
      case "object":
      case "array": {
        const raw = (data.valueJson || "").trim();
        if (!raw) {
          value = selectedSetting.type === "array" ? [] : {};
          break;
        }
        try {
          const parsed = JSON.parse(raw);
          if (selectedSetting.type === "array" && !Array.isArray(parsed)) {
            setError("valueJson", {
              type: "manual",
              message: "Value must be a JSON array",
            });
            return;
          }
          if (
            selectedSetting.type === "object" &&
            (Array.isArray(parsed) ||
              parsed === null ||
              typeof parsed !== "object")
          ) {
            setError("valueJson", {
              type: "manual",
              message: "Value must be a JSON object",
            });
            return;
          }
          value = parsed;
        } catch {
          setError("valueJson", {
            type: "manual",
            message: "Invalid JSON",
          });
          return;
        }
        break;
      }
    }

    const payload: AccountSettingPayload = {
      settingId: selectedSetting._id,
      value,
      active: data.active,
    };
    onSave(payload);
  };

  // When the selected setting changes (e.g. on new creation), if the
  // currently-held scalar value is empty, pre-fill from the Setting's
  // `default` so the user has a starting point.
  useEffect(() => {
    if (!open) return;
    if (!selectedSetting) return;
    // Only pre-fill for a brand new account setting, not for an edit.
    if (accountSetting) return;
    const def = selectedSetting.default;
    switch (selectedSetting.type) {
      case "string":
        setValue("valueString", typeof def === "string" ? def : "");
        break;
      case "number":
        setValue(
          "valueNumber",
          typeof def === "number" ? def : def === null ? "" : String(def ?? "")
        );
        break;
      case "boolean":
        setValue("valueBoolean", !!def);
        break;
      case "object":
      case "array":
        setValue("valueJson", safeStringify(def));
        break;
    }
  }, [selectedSetting, open, accountSetting, setValue]);

  const valueField = useMemo(() => {
    if (!selectedSetting) {
      return (
        <Typography variant="bodyMedium" color="text.secondary">
          Select a setting to configure its value.
        </Typography>
      );
    }
    switch (selectedSetting.type) {
      case "string":
        return (
          <Controller
            name="valueString"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value)}
                label="Value"
                fullWidth
                disabled={isPending}
              />
            )}
          />
        );
      case "number":
        return (
          <Controller
            name="valueNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value)}
                type="number"
                label="Value"
                fullWidth
                disabled={isPending}
              />
            )}
          />
        );
      case "boolean":
        return (
          <Controller
            name="valueBoolean"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!field.value}
                    onChange={(_e, checked) => field.onChange(checked)}
                    disabled={isPending}
                  />
                }
                label={`Value: ${field.value ? "true" : "false"}`}
              />
            )}
          />
        );
      case "object":
      case "array":
        return (
          <Controller
            name="valueJson"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value)}
                label={`Value (JSON ${selectedSetting.type})`}
                placeholder={
                  selectedSetting.type === "array" ? "[]" : "{\n  \n}"
                }
                fullWidth
                multiline
                minRows={4}
                maxRows={10}
                disabled={isPending}
                error={!!errors.valueJson}
                helperText={
                  errors.valueJson?.message ||
                  `Enter a valid JSON ${selectedSetting.type}.`
                }
              />
            )}
          />
        );
      default:
        return null;
    }
  }, [selectedSetting, control, errors.valueJson, isPending]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      theme={theme}
      title={isEdit ? "Edit Account Setting" : "Create Account Setting"}
      size={{
        width: "100%",
        minHeight: 480,
        maxHeight: 680,
        maxWidth: 640,
      }}
      content={
        <Box sx={{ width: "100%", padding: "20px 0" }}>
          <Grid container spacing={"20px"}>
            <Grid item xs={12}>
              <Autocomplete
                options={availableSettings}
                loading={isLoadingSettings}
                disabled={isPending || isEdit}
                getOptionLabel={(option) =>
                  option.name
                    ? `${option.name} (${option.module}/${option.section})`
                    : option._id
                }
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                value={
                  availableSettings.find((s) => s._id === selectedSettingId) ||
                  null
                }
                onChange={(_e, selected) => {
                  setValue("settingId", selected?._id || "", {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Setting"
                    placeholder="Select a setting"
                    required
                    error={!!errors.settingId}
                    helperText={
                      errors.settingId?.message ||
                      (isEdit ? "Setting cannot be changed on edit." : "")
                    }
                  />
                )}
              />
            </Grid>

            {selectedSetting && (
              <Grid item xs={12}>
                <Typography variant="bodyMedium" color="text.secondary">
                  Type: <strong>{selectedSetting.type}</strong>
                  {" \u2022 "}Application:{" "}
                  <strong>{selectedSetting.application}</strong>
                </Typography>
                {selectedSetting.description && (
                  <Typography
                    variant="bodyMedium"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {selectedSetting.description}
                  </Typography>
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              {valueField}
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(_e, checked) => field.onChange(checked)}
                        disabled={isPending}
                      />
                    }
                    label={field.value ? "Active" : "Inactive"}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default AccountSettingModal;
